import { motion } from "framer-motion";
import { ShieldCheck, Target, Lightning, HardHat, Eye, Crosshair } from "@phosphor-icons/react";
import { Separator } from "@/components/ui/separator";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };

const values = [
  { icon: <ShieldCheck size={32} weight="duotone" />, title: "Integrity", desc: "We conduct our business with the highest ethical standards, ensuring transparency and accountability in every transaction, project, and relationship." },
  { icon: <Target size={32} weight="duotone" />, title: "Excellence", desc: "We are committed to delivering superior quality through world-class products, precision engineering, and meticulous attention to detail in every endeavor." },
  { icon: <Lightning size={32} weight="duotone" />, title: "Synergy", desc: "Our integrated model combines equipment distribution, bitumen supply, and steel fabrication to create comprehensive, end-to-end infrastructure solutions." },
  { icon: <HardHat size={32} weight="duotone" />, title: "Safety", desc: "We prioritize the well-being of our people and communities, adhering to strict SABS and SANRAL standards in all operations and deliverables." },
];

const clients = [
  "Mining Houses", "Quarries", "Aggregate Producers", "Road Authorities",
  "Paving Contractors", "Civil Engineers", "Road Contractors", "Plant Operators", "Industrial Clients"
];

export default function About() {
  return (
    <div>
      {/* HERO */}
      <section className="relative py-28 lg:py-36 overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0">
          <img src="/images/projects/sugar-debagging-unit.jpeg" alt="KZNSC Sugar Debagging Unit" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0B2D2D]/80" />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#A7D129] mb-4">About Us</motion.p>
            <motion.h1 variants={fadeUp} className="font-heading font-black text-5xl md:text-7xl tracking-tighter text-white leading-[0.9] mb-6">
              KwaZulu Supplies<br />&amp; Construction
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-[#E0F5F2]/70 max-w-2xl leading-relaxed">
              A premier South African enterprise building the backbone of Southern African infrastructure through integrated supply, fabrication, and equipment solutions.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* CORPORATE OVERVIEW */}
      <section className="py-20 lg:py-28 bg-white" data-testid="corporate-overview">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00A091] mb-3">Corporate Overview</p>
                <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-[#0B2D2D] mb-8">Who We Are</h2>
                <div className="space-y-4 text-base text-[#0B2D2D]/70 leading-relaxed">
                  <p>
                    KwaZulu Supplies and Construction (Pty) Ltd (KZNSC) stands at the forefront of Southern Africa's infrastructure development. We specialize in supplying essential equipment, materials, and fabrication services to the mining, road infrastructure, and heavy construction sectors across the region.
                  </p>
                  <p>
                    Our company operates on a synergistic model that combines three key pillars: Equipment Distribution, Bitumen Supply, and Steel Fabrication. This integrated approach allows us to offer comprehensive solutions that address the full spectrum of infrastructure development needs.
                  </p>
                  <p>
                    As the authorized distributor for FTM (Henan Fote Heavy Machinery Co. Ltd) in Southern Africa, we bring world-class crushing, screening, and milling equipment to our clients. Combined with our high-grade bitumen products meeting SANRAL and international standards, and our precision steel fabrication capabilities, we deliver unmatched value across the infrastructure lifecycle.
                  </p>
                </div>
              </motion.div>
            </div>
            <div className="lg:col-span-5">
              <motion.div className="space-y-6" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                {/* Company Details Card */}
                <div className="bg-white border border-[#0F4C5C]/20 p-8">
                  <h3 className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#00A091] mb-4">Company Details</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between border-b border-[#0F4C5C]/10 pb-2">
                      <span className="text-[#0B2D2D]/50 font-mono text-xs">Legal Name</span>
                      <span className="font-semibold text-[#0B2D2D]">KwaZulu Supplies & Construction (Pty) Ltd</span>
                    </div>
                    <div className="flex justify-between border-b border-[#0F4C5C]/10 pb-2">
                      <span className="text-[#0B2D2D]/50 font-mono text-xs">Location</span>
                      <span className="font-semibold text-[#0B2D2D] text-right">Pietermaritzburg, KZN</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#0B2D2D]/50 font-mono text-xs">Scope</span>
                      <span className="font-semibold text-[#0B2D2D]">Southern Africa</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="py-20 lg:py-28 bg-white relative" data-testid="mission-vision">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div className="bg-[#E0F5F2] border border-[#0F4C5C]/20 p-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} data-testid="mission-card">
              <div className="text-[#00A091] mb-4"><Crosshair size={36} weight="duotone" /></div>
              <h3 className="font-heading font-bold text-2xl text-[#0B2D2D] mb-4">Our Mission</h3>
              <p className="text-base text-[#0B2D2D]/70 leading-relaxed">
                To drive regional development by supplying world-class machinery, quality road materials, and precision-engineered fabrication services. We aim to empower our partners with the tools and materials they need to build lasting infrastructure.
              </p>
            </motion.div>
            <motion.div className="bg-[#0F4C5C] border border-[#0F4C5C]/20 p-10" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} data-testid="vision-card">
              <div className="text-[#A7D129] mb-4"><Eye size={36} weight="duotone" /></div>
              <h3 className="font-heading font-bold text-2xl text-white mb-4">Our Vision</h3>
              <p className="text-base text-[#E0F5F2]/70 leading-relaxed">
                To be the most trusted and comprehensive provider of infrastructure-enabling solutions in Southern Africa. We envision a region where quality infrastructure drives economic growth and improves lives.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="py-20 lg:py-28 bg-[#E0F5F2]" data-testid="values-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div className="mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00A091] mb-3">Our Foundation</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-[#0B2D2D]">Core Values</h2>
          </motion.div>
          <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            {values.map((v, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-white border border-gray-100 p-8 rounded-sm hover-lift" data-testid={`about-value-${i}`}>
                <div className="text-[#00A091] mb-4">{v.icon}</div>
                <h3 className="font-heading font-bold text-lg text-[#0B2D2D] mb-3">{v.title}</h3>
                <p className="text-sm text-[#0B2D2D]/60 leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* TARGET CLIENTS */}
      <section className="py-20 bg-[#0F4C5C] relative noise-overlay" data-testid="clients-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
          <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#A7D129] mb-3">Who We Serve</p>
            <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight text-white">Our Clients</h2>
          </motion.div>
          <motion.div className="flex flex-wrap justify-center gap-4" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            {clients.map((c, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-white/10 border border-white/20 px-6 py-3">
                <span className="text-white font-semibold text-sm">{c}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
